import {
  LogEventCompressionMode,
  LogLevel,
  StatsigProvider,
  type StatsigUser,
} from '@statsig/react-bindings'
import { type ReactNode, useEffect, useMemo } from 'react'

import { logger } from '../logging/logger'
import { useClientAsyncInitRN } from '../statsig/useClientAsyncInitRN'

// React Native counterpart of @funkit/connect's FunkitStatsigProvider. Same Fun
// client key + custom proxy (the Statsig domain is usually blocked by adblockers
// — https://docs.statsig.com/infrastructure/api_proxy/custom_proxy), but the
// client is created with the RN bindings (AsyncStorage persistence, AppState
// visibility) via `useClientAsyncInitRN`. The web-only Datadog `values_updated`
// correlation is omitted (RN logging is wired separately).
const STATSIG_API = 'https://data-api.fun.xyz/v1'
export const STATSIG_CLIENT_KEY =
  'client-UmFd8WIJljA7cLmZuDqs3X25M8sKd5WIQP4BSC2bRbM' as const

// Hardcoded SDK marker (not UA/OS-parsed, so present on every eval). Lets a
// config shared with the web SDK be scoped per-SDK via a targeting rule.
export const STATSIG_SDK_TYPE = 'react-native' as const

/**
 * Statsig user for the RN SDK: `customIDs.apiKey` drives customer-gated configs;
 * `custom.sdkType` scopes shared configs to this SDK (see {@link STATSIG_SDK_TYPE}).
 */
export function buildFunkitStatsigUser(
  userId: string,
  apiKey: string,
): StatsigUser {
  return {
    userID: userId,
    customIDs: { apiKey },
    custom: { sdkType: STATSIG_SDK_TYPE },
  }
}

interface FunkitStatsigProviderProps {
  children: ReactNode
  /** Customer apiKey — gates customer-scoped configs (e.g. `enableswappeddeposit`). */
  apiKey: string
  /** Resolved funkit user id (apiKey|externalUserId, else wallet address). */
  userId: string
  isDevelopment?: boolean
}

export function FunkitStatsigProvider({
  children,
  apiKey,
  userId,
  isDevelopment = false,
}: FunkitStatsigProviderProps) {
  const user = useMemo<StatsigUser>(
    () => buildFunkitStatsigUser(userId, apiKey),
    [userId, apiKey],
  )

  const { client, isLoading } = useClientAsyncInitRN(STATSIG_CLIENT_KEY, user, {
    networkConfig: {
      api: STATSIG_API,
      // default timeout (10s) is too large; statsig usually responds <200ms
      networkTimeoutMs: 5_000,
    },
    // Our proxy supports compression; force it on to save bandwidth.
    logEventCompressionMode: LogEventCompressionMode.Forced,
    // Silence harmless warnings from hooks running before async init (we have
    // bootstrap fallbacks).
    logLevel: isDevelopment ? undefined : LogLevel.Error,
    environment: {
      tier: isDevelopment ? 'development' : 'production',
    },
  })

  useEffect(() => {
    // Statsig drops what it writes before its storage is ready; init awaits it.
    if (isLoading) {
      return
    }

    const { user: currentUser } = client.getContext()
    const apiKeyChanged = currentUser.customIDs?.apiKey !== apiKey
    const userIdChanged = currentUser.userID !== userId

    if (apiKeyChanged || userIdChanged) {
      logger.info('statsig:updateUser', { apiKey, userId })
      client
        .updateUserAsync(user)
        .catch((error) => logger.error('statsig:updateUser:error', error))
    }
  }, [client, isLoading, user, apiKey, userId])

  return <StatsigProvider client={client}>{children}</StatsigProvider>
}
